import React from 'react';
import { motion } from 'framer-motion';

interface CardProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  header_action?: React.ReactNode;
  footer?: React.ReactNode;
  is_hoverable?: boolean;
  is_clickable?: boolean;
  onClick?: (e?: React.MouseEvent) => void;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

export const Card: React.FC<CardProps> = ({
  children,
  title,
  subtitle,
  header_action,
  footer,
  is_hoverable = false,
  is_clickable = false,
  onClick,
  className = '',
  padding = 'md'
}) => {
  const padding_classes = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8'
  };
  
  const base_classes = 'bg-white rounded-lg shadow-sm border border-gray-200 transition-smooth';
  
  const interactive_classes = is_hoverable || is_clickable
    ? 'hover:shadow-md hover:border-gray-300'
    : '';
  
  const clickable_classes = is_clickable
    ? 'cursor-pointer active:scale-[0.99]'
    : '';
  
  const combined_classes = `${base_classes} ${interactive_classes} ${clickable_classes} ${className}`;
  
  const CardWrapper = is_clickable ? motion.div : 'div';
  const motion_props = is_clickable
    ? {
        whileHover: { scale: 1.01 },
        whileTap: { scale: 0.99 }
      }
    : {};
  
  return (
    <CardWrapper
      className={combined_classes}
      onClick={onClick}
      {...motion_props}
    >
      {/* Header */}
      {(title || subtitle || header_action) && (
        <div className={`border-b border-gray-200 ${padding_classes[padding]} pb-4`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {title && (
                <h3 className="text-lg font-semibold text-charcoal">
                  {title}
                </h3>
              )}
              {subtitle && (
                <p className="mt-1 text-sm text-gray-500">
                  {subtitle}
                </p>
              )}
            </div>
            {header_action && (
              <div className="ml-4 flex-shrink-0">
                {header_action}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className={title || subtitle || header_action ? padding_classes[padding] : padding_classes[padding]}>
        {children}
      </div>
      
      {/* Footer */}
      {footer && (
        <div className={`border-t border-gray-200 ${padding_classes[padding]} pt-4 bg-gray-50 rounded-b-lg`}>
          {footer}
        </div>
      )}
    </CardWrapper>
  );
};

interface CardGridProps {
  children: React.ReactNode;
  columns?: 1 | 2 | 3 | 4;
  gap?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const CardGrid: React.FC<CardGridProps> = ({
  children,
  columns = 3,
  gap = 'md',
  className = ''
}) => {
  const column_classes = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };
  
  const gap_classes = {
    sm: 'gap-3',
    md: 'gap-4',
    lg: 'gap-6'
  };
  
  return (
    <div className={`grid ${column_classes[columns]} ${gap_classes[gap]} ${className}`}>
      {children}
    </div>
  );
};